import { Button, Flex, type GetProps, Layout, Tooltip, Typography } from "antd";
import { StripeNotice } from "molecules/StripeNotice/StripeNotice";
import { buildStripeNoticeStatus } from "molecules/StripeNotice/buildStripeNoticeStatus";
import { showNewPaymentModal } from "components/PaymentsLanding/NewPaymentModal/showNewPaymentModal";
import { showTakePaymentModal } from "components/PaymentsLanding/NewPaymentModal/showTakePaymentModal";
import { usePaymentProcessor } from "auth/usePaymentProcessor";
import { useRouterState } from "@tanstack/react-router";
import { useStripeAccountStatus } from "api/clients/useGetStripeAccountStatus";

const { Header } = Layout;
const { Title } = Typography;

type ButtonProps = GetProps<typeof Button>;

// Open the modal, but we don't care about its lifetime expressed by the Promise
function newPaymentRequest() {
    void showNewPaymentModal();
}

function takeAPayment() {
    void showTakePaymentModal();
}

/** Header for the Payments system which displays a title and global action buttons */
export const PageHeader = () => {
    const { location } = useRouterState();
    const isHistory = location.pathname.startsWith("/history");
    const isStripe = usePaymentProcessor() === "Stripe";

    return <PageHeaderContent isHistory={isHistory} isStripe={isStripe} />;
};

interface PageHeaderContentProps {
    readonly isHistory: boolean;
    readonly isStripe: boolean;
}

export const PageHeaderContent = ({ isHistory, isStripe }: PageHeaderContentProps) => {
    return (
        <Flex
            component={Header}
            align="center"
            justify="space-between"
            role="banner"
            id="vyne-payments-page-header"
            vertical
        >
            {isHistory && isStripe && <StripeNotice />}
            <Flex className="vyne-payments-header-wrapper" align="center" justify="space-between">
                <Title>Payments</Title>
                {isHistory && (
                    <Flex align="flex-end" gap="small">
                        <PageHeaderButtons isStripe={isStripe} />
                    </Flex>
                )}
            </Flex>
        </Flex>
    );
};

/** Buttons for the page header */
const PageHeaderButtons = ({ isStripe }: { readonly isStripe: boolean }) => {
    if (isStripe) {
        return <StripeTooltipButtons />;
    }

    return (
        <>
            <TakeAPaymentButton />
            <NewPaymentRequestButton />
        </>
    );
};

interface PaymentButtonProps extends ButtonProps {
    readonly ref?: React.Ref<HTMLButtonElement | HTMLAnchorElement>;
}

const TakeAPaymentButton = (props: PaymentButtonProps) => (
    <Button {...props} size="large" type="default" onClick={takeAPayment}>
        Take a Payment
    </Button>
);

const NewPaymentRequestButton = (props: PaymentButtonProps) => (
    <Button {...props} size="large" type="primary" onClick={newPaymentRequest}>
        New Payment Request
    </Button>
);

/** Buttons for the page header, with wrapper tooltips on error */
const StripeTooltipButtons = () => {
    const { data } = useStripeAccountStatus();
    const { status } = buildStripeNoticeStatus(data);
    const hasStripeError = status === "error";

    return (
        <>
            <Tooltip title={hasStripeError ? `Stripe account must be updated to Take a Payment` : null}>
                <TakeAPaymentButton disabled={hasStripeError} />
            </Tooltip>
            <Tooltip title={hasStripeError ? `Stripe account must be updated to make a New Payment Request` : null}>
                <NewPaymentRequestButton disabled={hasStripeError} />
            </Tooltip>
        </>
    );
};
